export default function Hero() {
  return (
    <section className="relative min-h-screen flex items-center justify-center pt-20 px-6 overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://public.readdy.ai/ai/img_res/8111190e290d4ac14bf789e6a4818548.jpg"
          alt="Happy pets background"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/85 via-white/75 to-white/85"></div>
      </div>

      {/* Decorative Elements - Left Side */}
      <div className="absolute left-0 top-0 bottom-0 w-1/4 pointer-events-none hidden lg:flex flex-col justify-center items-center gap-8 z-10">
        {/* Floating Pet Icons */}
        <div className="animate-float" style={{ animationDelay: '0s' }}>
          <div className="w-20 h-20 bg-gradient-to-br from-pink-200/80 to-pink-300/80 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg">
            <span className="text-4xl">🐕</span>
          </div>
        </div>
        <div className="animate-float" style={{ animationDelay: '1s' }}>
          <div className="w-16 h-16 bg-gradient-to-br from-purple-200/80 to-purple-300/80 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg">
            <span className="text-3xl">🐈</span>
          </div>
        </div>
        <div className="animate-float" style={{ animationDelay: '2s' }}>
          <div className="w-24 h-24 bg-gradient-to-br from-mint-200/80 to-teal-200/80 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg">
            <span className="text-5xl">🐾</span>
          </div>
        </div>
        <div className="animate-float" style={{ animationDelay: '1.5s' }}>
          <div className="w-18 h-18 bg-gradient-to-br from-yellow-200/80 to-orange-200/80 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg">
            <span className="text-3xl">❤️</span>
          </div>
        </div>
      </div>

      {/* Decorative Elements - Right Side */}
      <div className="absolute right-0 top-0 bottom-0 w-1/4 pointer-events-none hidden lg:flex flex-col justify-center items-center gap-8 z-10">
        {/* Tech & Data Icons */}
        <div className="animate-float" style={{ animationDelay: '0.5s' }}>
          <div className="w-20 h-20 bg-gradient-to-br from-blue-200/80 to-indigo-300/80 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-lg">
            <i className="ri-database-2-line text-4xl text-indigo-600"></i>
          </div>
        </div>
        <div className="animate-float" style={{ animationDelay: '1.8s' }}>
          <div className="w-24 h-24 bg-gradient-to-br from-teal-200/80 to-cyan-300/80 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-lg">
            <i className="ri-line-chart-line text-5xl text-cyan-600"></i>
          </div>
        </div>
        <div className="animate-float" style={{ animationDelay: '2.3s' }}>
          <div className="w-16 h-16 bg-gradient-to-br from-rose-200/80 to-pink-300/80 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-lg">
            <i className="ri-robot-line text-3xl text-rose-600"></i>
          </div>
        </div>
        <div className="animate-float" style={{ animationDelay: '1.2s' }}>
          <div className="w-20 h-20 bg-gradient-to-br from-amber-200/80 to-orange-300/80 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-lg">
            <i className="ri-rocket-line text-4xl text-orange-600"></i>
          </div>
        </div>
      </div>

      {/* Centered Content */}
      <div className="relative z-10 max-w-[900px] w-full mx-auto text-center">
        <div className="bg-white/60 backdrop-blur-md rounded-3xl p-12 lg:p-16 shadow-[0_8px_32px_rgba(0,0,0,0.08)]">
          <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight mb-8 bg-gradient-to-r from-[#FF6B6B] to-[#6B5FFF] bg-clip-text text-transparent">
            Bor Studio – Incubating the Next Generation of Pet Startups in Asia
          </h1>
          
          <h2 className="text-lg lg:text-xl font-medium text-[#2D3748] mb-6 leading-relaxed">
            We support early-stage pet founders to turn great ideas into successful businesses.
          </h2>
          
          <p className="text-base text-[#4A5568] leading-relaxed mb-10 max-w-[750px] mx-auto">
            We provide hands-on guidance, expert networks, and strategic support to help founders overcome key challenges and grow faster. Our focus is on building strong, sustainable pet companies with long-term potential in Asia.
          </p>
          
          <a
            href="#contact"
            className="inline-flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-[#FF6B6B] to-[#6B5FFF] text-white text-lg font-bold rounded-full shadow-lg hover:shadow-xl hover:-translate-y-1 hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            Get in Touch
            <span className="text-2xl">🐾</span>
          </a>
        </div>
      </div>

      {/* Scattered Paw Prints */}
      <div className="absolute inset-0 pointer-events-none z-5">
        <div className="absolute top-[15%] left-[8%] text-pink-300/40 text-3xl animate-pulse" style={{ animationDelay: '0s' }}>🐾</div>
        <div className="absolute top-[25%] right-[12%] text-purple-300/40 text-2xl animate-pulse" style={{ animationDelay: '1s' }}>🐾</div>
        <div className="absolute bottom-[20%] left-[15%] text-teal-300/40 text-2xl animate-pulse" style={{ animationDelay: '2s' }}>🐾</div>
        <div className="absolute bottom-[30%] right-[10%] text-orange-300/40 text-3xl animate-pulse" style={{ animationDelay: '1.5s' }}>🐾</div>
        <div className="absolute top-[40%] left-[5%] text-pink-300/40 text-xl animate-pulse" style={{ animationDelay: '2.5s' }}>🐾</div>
        <div className="absolute top-[60%] right-[8%] text-purple-300/40 text-xl animate-pulse" style={{ animationDelay: '0.8s' }}>🐾</div>
      </div>
    </section>
  );
}